import React, { useState } from 'react';
import { Loader, Modal } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { useWoltVenueController } from './useWoltVenueController';
import classes from './wolt-venue-modal.module.scss';

type WoltVenueModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
};

// Manager control for the venue's availability on Wolt: current status + click-guarded toggle.
export const WoltVenueModal: React.FC<WoltVenueModalProps> = ({ opened, onClose, tenantId, employeeSession }) => {
  const { online, isLoading, isSaving, error, setVenueOnline } = useWoltVenueController({ tenantId, employeeSession, opened });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const next = !online;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span>{'וולט: סטטוס סניף'}</span>}
      centered
      radius="md"
      zIndex={302}
      closeButtonProps={{ 'data-testid': 'wolt-venue-close' } as Record<string, string>}
    >
      <div className={classes.stack} data-testid="wolt-venue-modal">
        {error && <p className={classes.error} data-testid="wolt-venue-error">{error}</p>}
        {isLoading ? (
          <div className={classes.center}>
            <Loader />
          </div>
        ) : online !== null ? (
          <>
            <div className={classes.statusRow}>
              <span className={classes.label}>{'הסניף כרגע'}</span>
              <span className={`${classes.pill} ${online ? classes.pillOnline : classes.pillOffline}`} data-testid="wolt-venue-status">
                {online ? 'פתוח' : 'סגור'}
              </span>
            </div>
            <Button
              variant={online ? 'cancel' : 'confirm'}
              fullWidth
              disabled={isSaving}
              onClick={() => setIsConfirmOpen(true)}
              dataTestId="wolt-venue-toggle"
            >
              {online ? 'סגור את הסניף בוולט' : 'פתח את הסניף בוולט'}
            </Button>
          </>
        ) : null}
      </div>
      <Modal
        opened={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={next ? 'לפתוח את הסניף בוולט?' : 'לסגור את הסניף בוולט?'}
        centered
        radius="md"
        zIndex={303}
        closeButtonProps={{ 'data-testid': 'wolt-venue-confirm-close' } as Record<string, string>}
      >
        <div className={classes.stack} data-testid="wolt-venue-confirm-modal">
          <p className={classes.text}>
            {next ? 'לקוחות יוכלו להזמין מהסניף דרך וולט.' : 'הסניף יוצג כסגור בוולט ולא יתקבלו הזמנות חדשות.'}
          </p>
          <div className={classes.actions}>
            <Button variant="cancel" fullWidth onClick={() => setIsConfirmOpen(false)} disabled={isSaving} dataTestId="wolt-venue-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              loading={isSaving}
              onClick={async () => {
                if (await setVenueOnline(next)) {
                  setIsConfirmOpen(false);
                }
              }}
              dataTestId="wolt-venue-confirm"
            >
              {next ? 'פתח' : 'סגור'}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};
